import { FloppyDiskIcon, ViewIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Tabs } from "shared/components/Tabs";
import {
  useSurveyStore,
  selectCanPreview,
  toQuestionPayload,
  toConditionPayload,
  validateSurvey,
  ValidationError
} from "../store";
import { View } from "../constants";
import { Dispatch, SetStateAction, useState } from "react";
import { PreviewModal } from "../slices/preview-modal";
import { landlordSurveyApi } from "api/landlord-surveys/landlord-surveys";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { ValidationsPopover } from "./ValidationsPopover";

export const LayoutHeader = ({
  view,
  setView
}: {
  view: View;
  setView: Dispatch<SetStateAction<View>>;
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isCreateMode = id === "add";

  const data = useSurveyStore((state) => state.data);
  const isDirty = useSurveyStore((state) => state.isDirty);
  const dirtyFields = useSurveyStore((state) => state.dirtyFields);
  const resetApiData = useSurveyStore((state) => state.resetApiData);
  const canPreview = useSurveyStore(selectCanPreview);
  const [showPreview, setShowPreview] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => {
      if (!data) throw new Error("No data to save");
      return landlordSurveyApi.createSurvey({
        name: data.name,
        is_default: data.is_default,
        questions: data.questions.map(toQuestionPayload),
        conditions: (data.conditions ?? []).map(toConditionPayload)
      });
    },
    onSuccess: (result) => {
      resetApiData(result);
      queryClient.invalidateQueries({ queryKey: ["LANDLORD_SURVEYS"] });
      navigate(`/landlord-surveys/${result.identifier}`, { replace: true });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to create survey"));
    }
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!data?.identifier) throw new Error("No identifier");
      return landlordSurveyApi.updateSurvey(data.identifier, {
        identifier: data.identifier,
        dirty_map: [...dirtyFields],
        name: data.name,
        is_default: data.is_default,
        questions: data.questions.map(toQuestionPayload),
        conditions: (data.conditions ?? []).map(toConditionPayload)
      });
    },
    onSuccess: (result) => {
      resetApiData(result);
      queryClient.invalidateQueries({ queryKey: ["LANDLORD_SURVEYS"] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to save survey"));
    }
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const validationErrors = useSurveyStore((state) => state.validationErrors);
  const setValidationErrors = useSurveyStore(
    (state) => state.setValidationErrors
  );
  const updateActiveQuestionIdx = useSurveyStore(
    (state) => state.updateActiveQuestionIdx
  );
  const updateActiveConditionIdx = useSurveyStore(
    (state) => state.updateActiveConditionIdx
  );

  const handleErrorNavigate = (error: ValidationError) => {
    if (error.type === "survey") return;

    if (error.type === "question") {
      setView(View.Questions);
      updateActiveQuestionIdx(error.index);
    } else {
      setView(View.Conditions);
      updateActiveConditionIdx(error.index);
    }
  };

  const handleSave = () => {
    const errors = validateSurvey(useSurveyStore.getState());
    setValidationErrors(errors);
    if (errors.length > 0) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    if (isCreateMode) createMutation.mutate();
    else updateMutation.mutate();
  };

  const crumbs = [
    {
      title: "Surveys",
      href: "/landlord-surveys"
    },
    {
      title: data?.name || "Unknown",
      isActive: true
    }
  ];
  return (
    <>
      <header className="h-[72px] flex items-center justify-between border-b border-alpha-80 px-6">
        <BreadCrumbs crumbs={crumbs} />

        <Tabs
          tabs={[View.Questions, View.Conditions]}
          selected={view}
          setActiveTab={(t) => setView(t)}
        />

        <div className="flex gap-2 items-center">
          <ValidationsPopover
            errors={validationErrors}
            onNavigate={handleErrorNavigate}
          />
          <Button
            HugeIcon={ViewIcon}
            size={ButtonSize.SM}
            disabled={!canPreview}
            tooltip={
              !canPreview ? "Add questions with titles to preview" : undefined
            }
            onClick={() => {
              const errors = validateSurvey(useSurveyStore.getState());
              setValidationErrors(errors);
              if (errors.length > 0) {
                toast.error("Please fix validation errors before previewing");
                return;
              }
              setShowPreview(true);
            }}
          >
            Preview
          </Button>

          <Button
            HugeIcon={FloppyDiskIcon}
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            disabled={!isDirty || isSaving}
            tooltip={isDirty ? undefined : "Make changes before saving"}
            onClick={handleSave}
            className="w-[128px]"
          >
            {isSaving ? "Saving…" : "Save survey"}
          </Button>
        </div>
      </header>
      {showPreview && <PreviewModal onClose={() => setShowPreview(false)} />}
    </>
  );
};
